import { When } from "react-if";
import { useState } from "react";
import {
  PortalLineItemField,
  PortalPaidMaxAgeUnit,
  accountingSystemSpecificSetting,
  ACCOUNTING_CONFIG,
} from "shared";

import { Button } from "../../../../../common/Atoms/Button";
import { Card } from "../../../../../common/Atoms/Card";
import { CardContent } from "../../../../../common/Atoms/CardContent";
import { ColorPicker } from "../../../../../common/Atoms/ColorPicker";
import { Divider } from "../../../../../common/Atoms/Divider";
import { FormDescription } from "../../../../../common/Atoms/FormDescription";
import { FormLabel } from "../../../../../common/Atoms/FormLabel";
import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { Modal } from "../../../../../common/Atoms/Modal";
import { Notification } from "../../../../../common/Atoms/Notification";
import { Select } from "../../../../../common/Atoms/Select";
import { Toggle } from "../../../../../common/Atoms/Toggle";
import { useAccountingSystemTerm } from "../../../../hooks/useAccountingSystemTerm";
import { useGeneralSettingsForm } from "../../../../hooks/useGeneralSettingsForm";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { SaveAndError } from "../SaveAndError";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { StatementColumnsEditor } from "../Statement/StatementColumnsEditor";

const lineItemFieldLabels: Record<PortalLineItemField, string> = {
  [PortalLineItemField.Description]: `Description`,
  [PortalLineItemField.Tracking]: `Tracking`,
  [PortalLineItemField.Quantity]: `Quantity`,
  [PortalLineItemField.UnitAmount]: `Amount (unit price)`,
  [PortalLineItemField.Total]: `Line total`,
};

const paidMaxAgeUnitOptions = [
  { label: `Months`, value: PortalPaidMaxAgeUnit.Months },
  { label: `Years`, value: PortalPaidMaxAgeUnit.Years },
];

export function PortalRoot() {
  const {
    formData,
    onChange,
    onChangeMultiple,
  } = useGeneralSettingsForm();
  const currentOrg = useGetSelectedOrganisation();
  const [columnsModalOpen, setColumnsModalOpen] = useState(false);

  function handleChange(e) {
    onChange(e.name, !formData[e.name]);
  }

  const hiddenLineItemFields = formData.portalLineItemHiddenFields || [];

  function toggleLineItemField(field: PortalLineItemField) {
    onChangeMultiple({
      portalLineItemHiddenFields: hiddenLineItemFields.includes(field) ?
        hiddenLineItemFields.filter(hidden => hidden !== field) :
        [...hiddenLineItemFields, field],
    });
  }

  const term = useAccountingSystemTerm(`Quote`);

  return (
    <Card>
      <CardContent className={ `pt-6` }>
        <TwoColFormRow
          heading={ `Portal Permissions` }
          description={ `Control the default access types for invoice & customer portals` }
        >
          <div className={ `space-y-4` }>
            <Toggle
              label={ `Secure Portal Mode?` }
              description={ `Should contacts be required to verify their email address before accessing the portal?` }
              name={ `requirePortalSecurity` }
              checked={ formData.requirePortalSecurity || false }
              onChange={ handleChange }
            />

            <Toggle
              label={ `Allowed to view all invoices, estimates & quotes?` }
              description={ `When accessed via the invoice / quote portal, can the customer see all other invoices / quotes addressed to them?` }
              name={ `portalViewAllInvoices` }
              checked={ formData.portalViewAllInvoices || false }
              onChange={ handleChange }
            />

            <Toggle
              label={ `Allowed to view all paid invoices?` }
              description={ `Is the customer allowed to see & download previously paid invoices?` }
              name={ `portalViewPaidInvoices` }
              checked={ formData.portalViewPaidInvoices || false }
              onChange={ handleChange }
            />

            <When condition={ !!formData.portalViewPaidInvoices }>
              <div className={ `flex justify-between gap-5` }>
                <div>
                  <FormLabel>
                    { `Max age of paid invoices` }
                  </FormLabel>
                  <FormDescription>
                    { `Hide paid invoices issued longer ago than this. Leave blank to show them all.` }
                  </FormDescription>
                </div>
                <div className={ `flex items-end gap-2 shrink-0` }>
                  <div className={ `w-24` }>
                    <InputTextAddon
                      name={ `portalPaidMaxAge` }
                      dataType={ `number` }
                      placeholder={ `No limit` }
                      value={ formData.portalPaidMaxAge ?? `` }
                      onChange={ ({ value }) => onChange(`portalPaidMaxAge`, value === null || value === `` ? null : Number(value)) }
                    />
                  </div>
                  <div className={ `w-32` }>
                    <Select
                      name={ `portalPaidMaxAgeUnit` }
                      options={ paidMaxAgeUnitOptions }
                      selected={ formData.portalPaidMaxAgeUnit ?? PortalPaidMaxAgeUnit.Years }
                      onChange={ e => onChange(`portalPaidMaxAgeUnit`, e.value) }
                    />
                  </div>
                </div>
              </div>
            </When>

            <When condition={ accountingSystemSpecificSetting(currentOrg, ACCOUNTING_CONFIG.SUPPORTS_CUSTOM_FIELDS) }>
              <div>

                <Toggle
                  label={ `Display Custom Fields` }
                  description={ `Allow the customer to view all custom fields. (Required for using custom fields in statements)` }
                  name={ `displayCustomFields` }
                  checked={ formData.displayCustomFields || false }
                  onChange={ handleChange }
                />

                <When condition={ formData.displayCustomFields }>
                  <Paragraph
                    variant={ `help` }
                    className={ `text-red-600` }
                  >
                    { `CAUTION: This may display fields with "Print on Form" disabled (hidden) in Quickbooks.` }
                  </Paragraph>
                </When>
              </div>
            </When>
          </div>
        </TwoColFormRow>

        <Divider weight={ `light` }/>

        <TwoColFormRow
          heading={ `Display & Layout` }
          description={ `Choose which columns customers see on the portal statement. These are set separately from your PDF statement columns.` }
        >
          <Button
            color={ `white` }
            onClick={ () => setColumnsModalOpen(true) }
          >
            { `Adjust displayed columns` }
          </Button>

          <Modal
            open={ columnsModalOpen }
            setOpen={ setColumnsModalOpen }
            title={ `Portal Statement Columns` }
            width={ `lg` }
          >
            <StatementColumnsEditor field={ `portalStatementColumns` } />
            <SaveAndError />
          </Modal>
        </TwoColFormRow>

        <Divider weight={ `light` }/>

        <TwoColFormRow
          heading={ `Invoice Line Items` }
          description={ `Choose which line item columns customers see on the invoice portal. Turn them all off to show only the totals.` }
        >
          <div className={ `space-y-4` }>
            { Object.values(PortalLineItemField).map(field => (
              <Toggle
                key={ field }
                label={ lineItemFieldLabels[field] }
                name={ field }
                checked={ !hiddenLineItemFields.includes(field) }
                onChange={ () => toggleLineItemField(field) }
              />
            )) }

            <Toggle
              label={ `Hide amounts on zero value lines` }
              description={ `Hide the quantity and amounts on lines that total zero, such as lines used as section headings.` }
              name={ `portalHideZeroLineAmounts` }
              checked={ formData.portalHideZeroLineAmounts || false }
              onChange={ handleChange }
            />
          </div>
        </TwoColFormRow>

        <Divider weight={ `light` }/>

        <TwoColFormRow
          heading={ `Promises & Queries` }
          description={ `Let customers submit payment promises and queries from the invoice portal` }
        >
          <div className={ `space-y-4` }>
            <Toggle
              label={ `Allow customers to submit payment promises` }
              description={ `Customers can tell you when they will pay an unpaid invoice from the invoice portal. Policies with "skip when payment promised" wait until the promised date.` }
              name={ `portalPromisesEnabled` }
              checked={ formData.portalPromisesEnabled || false }
              onChange={ handleChange }
            />

            <When condition={ !!formData.portalPromisesEnabled }>
              <Toggle
                label={ `Require verification for payment promises` }
                description={ `Customers must verify their email address before submitting a payment promise, even when secure portal mode is off.` }
                name={ `portalPromiseRequiresVerification` }
                checked={ formData.portalPromiseRequiresVerification || false }
                onChange={ handleChange }
              />
            </When>

            <Toggle
              label={ `Allow customers to query invoices` }
              description={ `Customers can tell you what is wrong with an unpaid invoice from the invoice portal. Policies with "skip while a query is open" pause until it is resolved.` }
              name={ `portalQueriesEnabled` }
              checked={ formData.portalQueriesEnabled || false }
              onChange={ handleChange }
            />

            <When condition={ !!formData.portalQueriesEnabled }>
              <Toggle
                label={ `Require verification for queries` }
                description={ `Customers must verify their email address before raising a query on an invoice, even when secure portal mode is off.` }
                name={ `portalQueryRequiresVerification` }
                checked={ formData.portalQueryRequiresVerification || false }
                onChange={ handleChange }
              />
            </When>
          </div>
        </TwoColFormRow>

        <Divider weight={ `light` }/>

        <TwoColFormRow
          heading={ `Branding` }
          description={ `` }
        >
          <div className={ `flex justify-between` }>
            <div>
              <FormLabel>
                { `Branding Color` }
              </FormLabel>
              <FormDescription>
                { `Set the main color to appear in the portal.` }
              </FormDescription>
            </div>
            <ColorPicker
              value={ formData.primaryColor }
              onChange={ value => onChange(`primaryColor`, value) }
            />
          </div>
        </TwoColFormRow>

        <Divider weight={ `light` }/>

        <TwoColFormRow
          heading={ `${term} Settings` }
          description={ `` }
        >
          <div className={ `space-y-4` }>
            <Toggle
              label={ `Allowed to view ${term?.toLowerCase()}s?` }
              description={ `Allow customer to view ${term?.toLowerCase()}s in the portal` }
              name={ `portalShowQuotes` }
              checked={ formData.portalShowQuotes || false }
              onChange={ handleChange }
            />

            <Toggle
              label={ `Enable PO number` }
              description={ `Allow customers to enter a Purchase Order number upon ${term?.toLowerCase()} acceptance.` }
              name={ `allowedQuotePOField` }
              checked={ formData.allowedQuotePOField || false }
              onChange={ handleChange }
            />

            <Toggle
              label={ `Enable PO Document Upload` }
              description={ `Allow customers to a Purchase Order document upon ${term?.toLowerCase()} acceptance.` }
              helpIcon={ `Must be in a PDF format and less than 50MB` }
              name={ `allowedQuotePOUpload` }
              checked={ formData.allowedQuotePOUpload || false }
              onChange={ handleChange }
            />
          </div>
        </TwoColFormRow>

        <Notification
          className={ `mt-6` }
          type={ `info` }
        >
          { `Logo and payment terms/footer are set under general settings` }
        </Notification>
        <SaveAndError
        />
      </CardContent>
    </Card>
  );
}
